import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { usePostHog } from "posthog-js/react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import {
  LockClosedIcon,
  CalendarDaysIcon,
  CreditCardIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/solid";
import config from "config";

import Button from "components/Button";
import CallButton from "components/CallButton";
import {
  apiErrorCode,
  getApiErrorMessage,
  isTerminalApiError,
} from "utils/errors";

import {
  usePaymentMethods,
  useCreateSetupIntent,
  useConfirmPaymentMethod,
  useConfirmPayment,
} from "hooks/useArrangementAPI";
import {
  INPUT_CLASS_WITH_PLACEHOLDER as INPUT_CLASS,
  US_STATES,
} from "./constants";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type PaymentMethod = {
  record_id: string;
  type: string;
  last_4: string;
};

type PaymentSectionProps = {
  onComplete: () => void;
};

/**
 * A payment step that failed, and whether anything the family does can clear
 * it. `terminal` means only staff can — the UI offers the care team's number
 * rather than an action that will fail the same way every time.
 */
type PaymentFailure = {
  message: string;
  terminal: boolean;
};

const GENERIC_SETUP_FAILURE =
  "We could not open the secure payment form. Please try again.";

// ---------------------------------------------------------------------------
// Stripe Element styling
// ---------------------------------------------------------------------------

// Match the app's standard text inputs: evergreen text and a soil/25%
// placeholder (rgba spells out soil #503a3a at the same 25% opacity the
// `placeholder:text-soil placeholder:text-opacity-25` utilities give the HTML
// inputs below). fontFamily inherits the app font so the two field types match.
const STRIPE_ELEMENT_STYLE = {
  base: {
    fontSize: "16px",
    color: "#45594E",
    fontFamily: "inherit",
    "::placeholder": { color: "rgba(80, 58, 58, 0.25)" },
  },
  invalid: { color: "#dc2626" },
};

const STRIPE_WRAPPER =
  "w-full rounded-md border border-olive bg-white px-4 py-3 focus-within:border-evergreen focus-within:ring-1 focus-within:ring-evergreen";

// ---------------------------------------------------------------------------
// Stripe card fields — renders inside Stripe Elements provider
// ---------------------------------------------------------------------------

function StripeCardFields() {
  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1 block text-sm font-medium text-evergreen">
          Card Number
        </label>
        <div className={STRIPE_WRAPPER}>
          <CardNumberElement options={{ style: STRIPE_ELEMENT_STYLE }} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-evergreen">
            Expiration
          </label>
          <div className={STRIPE_WRAPPER}>
            <CardExpiryElement options={{ style: STRIPE_ELEMENT_STYLE }} />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-evergreen">
            CVC
          </label>
          <div className={STRIPE_WRAPPER}>
            <CardCvcElement options={{ style: STRIPE_ELEMENT_STYLE }} />
          </div>
        </div>
      </div>
    </div>
  );
}

// Billing fields — only collected in add-card mode; sent to Stripe (billing
// details on the PaymentMethod), never to our backend.
function BillingFields({
  cardName,
  setCardName,
  address,
  setAddress,
  apt,
  setApt,
  city,
  setCity,
  state,
  setState,
  zip,
  setZip,
}: {
  cardName: string;
  setCardName: (v: string) => void;
  address: string;
  setAddress: (v: string) => void;
  apt: string;
  setApt: (v: string) => void;
  city: string;
  setCity: (v: string) => void;
  state: string;
  setState: (v: string) => void;
  zip: string;
  setZip: (v: string) => void;
}) {
  return (
    <div className="space-y-4">
      <h3 className="pt-2 font-bold text-evergreen">Billing address</h3>
      <div>
        <label className="mb-1 block text-sm font-medium text-evergreen">
          Cardholder Name
        </label>
        <input
          type="text"
          autoComplete="cc-name"
          className={INPUT_CLASS}
          placeholder="Full name on card"
          value={cardName}
          onChange={(e) => setCardName(e.target.value)}
        />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-evergreen">
          Address
        </label>
        <input
          type="text"
          autoComplete="billing address-line1"
          className={INPUT_CLASS}
          placeholder="Street address"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
        />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-evergreen">
          Apt / Suite
        </label>
        <input
          type="text"
          autoComplete="billing address-line2"
          className={INPUT_CLASS}
          placeholder="Apartment, suite, etc."
          value={apt}
          onChange={(e) => setApt(e.target.value)}
        />
      </div>
      {/* City spans full width on mobile with State/ZIP sharing a row; all
          three sit inline at sm and up. */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <div className="col-span-2 sm:col-span-1">
          <label className="mb-1 block text-sm font-medium text-evergreen">
            City
          </label>
          <input
            type="text"
            autoComplete="billing address-level2"
            className={INPUT_CLASS}
            placeholder="City"
            value={city}
            onChange={(e) => setCity(e.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-evergreen">
            State
          </label>
          <select
            autoComplete="billing address-level1"
            className={INPUT_CLASS}
            value={state}
            onChange={(e) => setState(e.target.value)}
          >
            <option value="">State</option>
            {US_STATES.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-evergreen">
            ZIP
          </label>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="billing postal-code"
            maxLength={10}
            className={INPUT_CLASS}
            placeholder="12345"
            value={zip}
            onChange={(e) => setZip(e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}

// Save-card button — renders inside the Elements provider. Confirms the card
// with Stripe, persists the PaymentMethod to our backend (billing_* NOT sent),
// then hands the saved record_id back so the parent can select it.
function SaveCardButton({
  clientSecret,
  billingDetails,
  onSaved,
}: {
  clientSecret: string;
  billingDetails: Record<string, unknown>;
  onSaved: (recordId: string) => void | Promise<void>;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { caseId } = useParams<{ caseId: string }>();
  const [error, setError] = useState<PaymentFailure | null>(null);
  const [saving, setSaving] = useState(false);

  const { trigger: savePaymentMethod } = useConfirmPaymentMethod(caseId);

  const fail = (message: string, terminal = false) => {
    setError({ message, terminal });
    setSaving(false);
  };

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    setError(null);
    setSaving(true);

    const cardNumber = elements.getElement(CardNumberElement);
    if (!cardNumber) {
      fail("Card form not ready.");
      return;
    }

    const result = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: cardNumber,
        billing_details: billingDetails,
      },
    });

    if (result.error) {
      fail(result.error.message || "Payment setup failed.");
      return;
    }

    if (result.setupIntent?.payment_method) {
      const pm = result.setupIntent.payment_method;
      const pmId = typeof pm === "string" ? pm : pm.id;
      const last4 = typeof pm === "object" ? pm.card?.last4 || "" : "";

      try {
        const saved = (await savePaymentMethod({
          payment_method: pmId,
          type: "card",
          last_4: last4,
        })) as { record_id?: string } | undefined;
        if (!saved?.record_id) {
          fail("Failed to save payment method.");
          return;
        }
        await onSaved(saved.record_id);
      } catch (err) {
        // Stripe has already captured the card by this point, so the reason
        // this failed is the only useful thing we can show — a bare "failed"
        // leaves the family retrying a card that saved fine on Stripe's side.
        // The same purchaser the Setup Intent checked is checked again here,
        // so this can land on a cause no re-submit will clear.
        const code = apiErrorCode(err);
        fail(
          getApiErrorMessage(code, "Failed to save payment method."),
          isTerminalApiError(code),
        );
      }
    } else {
      // confirmCardSetup resolved without an error but also without a
      // payment_method (e.g. requires_action). Reset so the button isn't
      // left spinning with no way to retry.
      fail("Payment setup didn't complete. Please try again.");
    }
  };

  return (
    <>
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-700">{error.message}</p>
        </div>
      )}
      {error?.terminal ? (
        <CallButton />
      ) : (
        <Button
          onClick={handleSubmit}
          disabled={!stripe || saving}
          variant="primary"
          size="lg"
          fullWidth
        >
          {saving ? "Saving your card..." : "Save this card"}
        </Button>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Main PaymentSection
// ---------------------------------------------------------------------------

/**
 * Stands in for the card fields until the Setup Intent resolves, and reports it
 * when it doesn't. A failure only staff can clear offers a phone number rather
 * than a Retry button, which would send the family round a loop that fails the
 * same way every time.
 */
function CardDetailsPlaceholder({
  error,
  onRetry,
}: {
  error: PaymentFailure | null;
  onRetry: () => void;
}) {
  if (!error) {
    return (
      <div className="space-y-4">
        <h3 className="font-bold text-evergreen">Card Details</h3>
        <div className="py-4 text-center text-soil">
          Initializing secure payment form...
        </div>
      </div>
    );
  }

  if (error.terminal) {
    return (
      <div className="space-y-4">
        <h3 className="font-bold text-evergreen">Card Details</h3>
        <div className="py-4 text-center">
          <p className="text-red-600">{error.message}</p>
          <div className="mx-auto mt-4 max-w-xs">
            <CallButton variant="compact" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="font-bold text-evergreen">Card Details</h3>
      <div className="py-4 text-center">
        <p className="text-red-600">{error.message}</p>
        <Button variant="primary" onClick={onRetry} className="mt-3">
          Retry
        </Button>
      </div>
    </div>
  );
}

export default function PaymentSection({ onComplete }: PaymentSectionProps) {
  const { caseId } = useParams<{ caseId: string }>();
  const posthog = usePostHog();

  const {
    data: pmData,
    isLoading: pmLoading,
    mutate: mutatePaymentMethods,
  } = usePaymentMethods(caseId);
  const { trigger: createSetupIntent } = useCreateSetupIntent(caseId);
  const { trigger: confirmPayment, isMutating: isConfirming } =
    useConfirmPayment(caseId);

  const existingMethods: PaymentMethod[] = useMemo(
    () => pmData?.payment_methods || [],
    [pmData],
  );

  const [mode, setMode] = useState<"select" | "add">("select");
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [setupIntentError, setSetupIntentError] =
    useState<PaymentFailure | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  // Billing state (add mode only)
  const [cardName, setCardName] = useState("");
  const [address, setAddress] = useState("");
  const [apt, setApt] = useState("");
  const [city, setCity] = useState("");
  const [state, setBillingState] = useState("");
  const [zip, setZip] = useState("");

  // Initialize Stripe on the platform account (no stripeAccount option)
  const stripePromise = useMemo(() => {
    const key = config.stripePublishableKey;
    if (!key) return null;
    return loadStripe(key);
  }, []);

  // Default selection + mode once the PM list resolves:
  // exactly one → pre-select it; multiple → none selected; zero → add-card mode.
  useEffect(() => {
    if (pmLoading || hasInitialized) return;
    if (existingMethods.length === 1) {
      setSelectedRecordId(existingMethods[0].record_id);
      setMode("select");
    } else if (existingMethods.length === 0) {
      setMode("add");
    } else {
      setMode("select");
    }
    setHasInitialized(true);
  }, [pmLoading, existingMethods, hasInitialized]);

  // Create a Setup Intent when entering add-card mode.
  //
  // The card fields only mount once this succeeds, so its failure is the whole
  // payment step for the family — which is why the reason is kept rather than
  // collapsed into a flag. Some causes (no contact details on the case) can
  // only be fixed by staff, and offering Retry for those sends the family round
  // a loop that always fails.
  const initSetupIntent = useCallback(async () => {
    if (clientSecret) return;
    setSetupIntentError(null);
    try {
      const result = await createSetupIntent({});
      if (result?.client_secret) {
        setClientSecret(result.client_secret);
      } else {
        setSetupIntentError({
          message: GENERIC_SETUP_FAILURE,
          terminal: false,
        });
      }
    } catch (err) {
      const code = apiErrorCode(err);
      setSetupIntentError({
        message: getApiErrorMessage(code, GENERIC_SETUP_FAILURE),
        terminal: isTerminalApiError(code),
      });
    }
  }, [createSetupIntent, clientSecret]);

  useEffect(() => {
    if (mode === "add" && !clientSecret && !pmLoading) {
      initSetupIntent();
    }
  }, [mode, clientSecret, pmLoading, initSetupIntent]);

  // After a new card is saved: refresh the list, select it, return to select mode
  const handleCardSaved = async (recordId: string) => {
    await mutatePaymentMethods();
    setSelectedRecordId(recordId);
    setClientSecret(null);
    setMode("select");
  };

  // Step 2: confirm payment with the selected PM's record_id
  const handleConfirmPayment = async () => {
    if (!selectedRecordId) return;
    setConfirmError(null);
    try {
      await confirmPayment({ payment_method_id: selectedRecordId });
      onComplete();
    } catch (err) {
      // NetworkError carries the parsed body but an empty `message`, so map
      // the error_code to a friendly string (falling back to the server's
      // error text, then a generic message) instead of showing "".
      const responseData = (
        err as { responseData?: { error?: string; error_code?: string } }
      )?.responseData;
      setConfirmError(
        getApiErrorMessage(
          responseData?.error_code,
          responseData?.error || "Something went wrong. Please try again.",
        ),
      );
    }
  };

  // Switch to the add-card form, recording the family's intent to use a new card
  const handleAddNewCard = () => {
    posthog?.capture("arrangements_payment_new_card", { case_id: caseId });
    setMode("add");
  };

  if (pmLoading) {
    return (
      <div className="mx-auto flex max-w-lg items-center justify-center py-12">
        <div className="text-soil">Loading payment information...</div>
      </div>
    );
  }

  const trustCard = (
    <div className="rounded-md border border-oat bg-oat p-4 text-sm text-soil">
      <div className="flex items-start gap-3">
        <CalendarDaysIcon className="mt-0.5 h-5 w-5 shrink-0" />
        <div className="space-y-1">
          <p className="font-semibold">You won&apos;t be charged today</p>
          <p className="leading-relaxed">
            We&apos;re saving your card on file. Your Care Specialist will walk
            through pricing and confirm with you before any charge is processed.
          </p>
        </div>
      </div>
      <div className="mt-3 flex items-start gap-3 border-t border-plum/50 pt-3">
        <LockClosedIcon className="mt-0.5 h-5 w-5 shrink-0" />
        <div className="space-y-1">
          <p className="font-semibold">Safe and secure</p>
          <p className="leading-relaxed">
            Your card is protected with bank-grade 256-bit SSL encryption.
          </p>
        </div>
      </div>
      <div className="mt-3 flex items-start gap-3 border-t border-plum/50 pt-3">
        <DocumentTextIcon className="mt-0.5 h-5 w-5 shrink-0" />
        <div className="space-y-1">
          <p className="font-semibold">Pre-planned with us?</p>
          <p className="leading-relaxed">
            Your care specialist will confirm what your policy covers, and
            whether any balance is owed, before any charge is processed.
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-lg">
      {mode === "select" ? (
        <div className="space-y-3">
          <div className="space-y-1">
            <h3 className="font-bold text-evergreen">
              We saved this card when you spoke with our team.
            </h3>
            <p className="text-sm text-soil">
              Confirm it below, or use a different one.
            </p>
          </div>

          {existingMethods.map((pm) => {
            const isSelected = selectedRecordId === pm.record_id;
            return (
              <button
                key={pm.record_id}
                onClick={() => setSelectedRecordId(pm.record_id)}
                className={`flex w-full items-center gap-3 rounded-lg border p-4 text-left transition ${
                  isSelected
                    ? "border-evergreen bg-ivory ring-1 ring-evergreen"
                    : "border-olive bg-white hover:border-sage"
                }`}
              >
                {isSelected ? (
                  <CheckCircleIcon className="h-5 w-5 shrink-0 text-evergreen" />
                ) : (
                  <div className="h-5 w-5 shrink-0 rounded-full border-2 border-olive" />
                )}
                <CreditCardIcon className="h-5 w-5 shrink-0 text-soil" />
                <span className="font-medium text-evergreen">
                  Card ending in {pm.last_4 ? `****${pm.last_4}` : "****"}
                </span>
              </button>
            );
          })}

          <button
            onClick={handleAddNewCard}
            className="flex w-full items-center gap-3 rounded-lg border border-dashed border-olive bg-white p-4 text-left transition hover:border-sage"
          >
            <div className="h-5 w-5 shrink-0 rounded-full border-2 border-olive" />
            <span className="font-medium text-evergreen">Add a new card</span>
          </button>

          {trustCard}

          {confirmError && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3">
              <p className="text-sm text-red-700">{confirmError}</p>
            </div>
          )}

          <div className="flex flex-col items-end gap-2 pt-2">
            <Button
              onClick={handleConfirmPayment}
              disabled={!selectedRecordId || isConfirming}
              variant={selectedRecordId ? "primary" : "outline"}
            >
              {isConfirming ? "Confirming…" : "Confirm & Continue"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {existingMethods.length > 0 && (
            <button
              type="button"
              onClick={() => setMode("select")}
              className="flex items-center gap-1 p-0 text-sm text-evergreen underline"
            >
              <ArrowLeftIcon className="h-4 w-4 shrink-0" />
              Use a saved card
            </button>
          )}

          <BillingFields
            cardName={cardName}
            setCardName={setCardName}
            address={address}
            setAddress={setAddress}
            apt={apt}
            setApt={setApt}
            city={city}
            setCity={setCity}
            state={state}
            setState={setBillingState}
            zip={zip}
            setZip={setZip}
          />

          {clientSecret && stripePromise ? (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance: {
                  theme: "stripe",
                  variables: { colorPrimary: "#2D4A3E", borderRadius: "6px" },
                },
              }}
            >
              <div className="space-y-4">
                <h3 className="font-bold text-evergreen">Card Details</h3>
                <StripeCardFields />
                {trustCard}
                <SaveCardButton
                  clientSecret={clientSecret}
                  billingDetails={{
                    name: cardName || undefined,
                    address: {
                      line1: address || undefined,
                      line2: apt || undefined,
                      city: city || undefined,
                      state: state || undefined,
                      postal_code: zip || undefined,
                      country: "US",
                    },
                  }}
                  onSaved={handleCardSaved}
                />
              </div>
            </Elements>
          ) : (
            <CardDetailsPlaceholder
              error={setupIntentError}
              onRetry={() => {
                setClientSecret(null);
                initSetupIntent();
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
